import { process, State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { GridDataStateChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import GridWrapper, { GridColumn } from 'src/components/GridWrapper';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import exportToExcel from 'src/utils/exportToExcel';
import AttributesTableToolbar, { AttributesType } from './AttributesTableToolbar';

interface props {
  openAssignmentsModal: boolean;
  toggleAssignmentsModal: () => void;
  GLCodeAttributesData: AttributesType[];
  title: string;
}

const initialDataState: State = {
  skip: 0,
  take: 10,
  sort: [{ field: 'glCode', dir: 'asc' }],
};

const DATA_ITEM_KEY: string = 'rowId';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 1,
  pageSizes: [10, 20, 50, 100],
};

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};

const defaultProps = {
  tableTextId: 'Assignments',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
};

const AttributesTable = ({
  openAssignmentsModal,
  toggleAssignmentsModal,
  GLCodeAttributesData,
  title,
}: props) => {
  const { t } = useViewCheckbook();
  const ref = useRef(null);

  const GLCodeAttributesColumns: GridColumn<any>[] = useMemo(
    () => [
      {
        field: 'glCode',
        title: t('GL Code'),
        filter: 'text',
      },
      {
        field: 'description',
        title: t('Description'),
        filter: 'text',
      },
      {
        field: 'invoice',
        title: t('Invoice/PO Approval Attribute'),
        filter: 'text',
      },
      {
        field: 'user',
        title: t('Contact Assigned'),
        filter: 'text',
      },
    ],
    [t]
  );

  const [dataState, setDataState] = useState(initialDataState);
  const onDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  }, []);

  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...initialDataState,
      skip: prev.skip,
      take: prev.take,
    }));
  }, []);

  const { data, total } = useMemo(
    () => process(GLCodeAttributesData, dataState),
    [dataState, GLCodeAttributesData]
  );

  const excelExport = useCallback(
    (rows: any) => {
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      GLCodeAttributesColumns.forEach((item) => {
        columns.push(item.field as string);
        Object.assign(columnMapping, { [item.field as string]: item.title });
      });
      exportToExcel({
        columns,
        rows,
        columnMapping,
        fileName: 'GL Code Attributes and Assignments',
      });
    },
    [GLCodeAttributesColumns]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <AttributesTableToolbar
        clearFilter={handleClearFilter}
        dataState={dataState}
        rows={data}
        allData={GLCodeAttributesData}
        excelExport={excelExport}
      />
    ),
    [GLCodeAttributesData, data, dataState, excelExport, handleClearFilter]
  );

  const handleAssignmentsModalClose = useCallback(() => {
    toggleAssignmentsModal();
    setDataState(initialDataState);
  }, [toggleAssignmentsModal]);

  useEffect(() => {
    if (openAssignmentsModal) {
      (ref.current as any)?.focus?.();
    }
  }, [openAssignmentsModal]);

  return (
    <Dialog
      width="70%"
      id="modal"
      title={title}
      open={openAssignmentsModal}
      onClose={handleAssignmentsModalClose}
    >
      <GridWrapper
        id="AttributesTable"
        columns={GLCodeAttributesColumns}
        dataSource={data}
        total={total}
        gridToolbarChildren={gridToolbarChildren}
        onDataStateChange={onDataStateChange}
        dataState={dataState}
        {...defaultProps}
        className="attributes-table"
      />
      <DialogActionsBar>
        <div className="flex justify-end">
          <Button onClick={handleAssignmentsModalClose} color="info" ref={ref}>
            {t('Close')}
          </Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};
export default AttributesTable;
